import {
  Button,
  Input,
  Label,
  RangeSlider,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Slider,
  useToast,
} from '@ais-ui/ui-core';
import React, { useEffect } from 'react';
import { CustomizePopover } from './CustomizePopover';
import { densityReportMiscellaneous } from '../reportsTypes';
import { ParsedReportParams, useDensityReconfigure } from '@ais-ui/websockets';

import {
  getBackendColorScaleFromFrontend,
  getFrontendColorScaleFromBackend,
} from '../Mutations/utils/densityColorMapping';
import { useFetchOverlayData } from '../useMapReportOverlay';
import { markErrorHandledLocally } from '@ais-ui/custom-hooks';

interface CustomizeDensityProps {
  report: ParsedReportParams;
  className?: string;
}

export const CustomizeDensity = ({
  report,
  className,
}: CustomizeDensityProps) => {
  const fetchOverlayData = useFetchOverlayData();

  const [minMaxShipsThreshold, setMinMaxShipsThreshold] = React.useState({
    min: 1,
    max: 256,
  });

  const [minMaxShips, setMinMaxShips] = React.useState({
    min: report.params.SMMLMIN
      ? report.params.SMMLMIN
      : minMaxShipsThreshold.min,
    max: report.params.SMMLMAX
      ? report.params.SMMLMAX
      : minMaxShipsThreshold.max,
  });

  const { mutate } = useDensityReconfigure(
    fetchOverlayData,
    minMaxShipsThreshold,
    setMinMaxShipsThreshold,
    setMinMaxShips,
  );

  const [colorScale, setColorScale] = React.useState(
    getFrontendColorScaleFromBackend(report.params.CS),
  );

  // We need to run this when the component mounts because we need to set the initial values
  //  of maximum ships which are only present if we set maxShips to None. The backend should
  //  send this in another way, so we don't have to do this.
  useEffect(() => {
    mutate({
      jobId: report.info?.['Job ID'] as string,
      colorScale: getBackendColorScaleFromFrontend(colorScale),
      numberOfColors,
      smoothing,
      minShips: minMaxShips.min,
    });
  }, []);

  const [smoothing, setSmoothing] = React.useState(1);
  const [numberOfColors, setNumberOfColors] = React.useState(report.params.CD);
  const { toast } = useToast();

  const onApply = () => {
    mutate(
      {
        jobId: report.info?.['Job ID'] as string,
        colorScale: getBackendColorScaleFromFrontend(colorScale),
        numberOfColors,
        smoothing,
        minShips: minMaxShips.min,
        maxShips: minMaxShips.max,
      },
      {
        onSuccess: () => {
          toast({
            title: 'Success!',
            description: 'Customized density report successfully',
          });
        },
        onError: (error) => {
          markErrorHandledLocally(error);
          toast({
            title: 'Error!',
            description: 'Error customizing density report',
            variant: 'destructive',
          });
        },
      },
    );
  };

  return (
    <CustomizePopover>
      <form className="grid grid-cols-2 gap-8 p-2 w-96">
        <div className="space-y-2">
          <Label htmlFor="color-palette">Color Palette</Label>
          <Select value={colorScale} onValueChange={setColorScale}>
            <SelectTrigger className="w-full">
              <SelectValue placeholder="Select a color scale" />
            </SelectTrigger>
            <SelectContent>
              {densityReportMiscellaneous?.colorScale?.options?.map(
                (colorScale) => (
                  <SelectItem key={colorScale.value} value={colorScale.value}>
                    {colorScale.label}
                  </SelectItem>
                ),
              )}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-4">
          <div className="flex flex-col space-y-2">
            <Label htmlFor="minmaxships">Min and max vessels</Label>
            <Label className="text-muted-foreground">
              {minMaxShips.min} - {minMaxShips.max}
            </Label>
          </div>

          <RangeSlider
            id="minmaxships"
            value={[minMaxShips.min, minMaxShips.max]}
            onValueChange={(value) => {
              const [value1, value2] = value;
              if (value1 > value2) {
                setMinMaxShips({ min: value2, max: value1 });
              } else {
                setMinMaxShips({ min: value1, max: value2 });
              }
            }}
            min={minMaxShipsThreshold.min}
            max={minMaxShipsThreshold.max}
            step={1}
            className="w-full"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="num-colors">Number of Colors</Label>
          <Input
            id="num-colors"
            type="number"
            min="2"
            max="40"
            value={numberOfColors}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              setNumberOfColors(parseInt(event.target.value));
            }}
            className="w-full"
          />
        </div>
        <div className="space-y-4">
          <div className="flex flex-col space-y-2">
            <Label htmlFor="smoothing">Smoothing</Label>
            <Label className="text-muted-foreground">{smoothing}</Label>
          </div>
          <Slider
            id="smoothing"
            value={[smoothing]}
            onValueChange={(value) => {
              setSmoothing(value[0]);
            }}
            min={1}
            max={50}
            step={1}
            className="w-full"
          />
        </div>
      </form>
      <div className="pt-6 flex justify-end">
        <Button onClick={onApply}>Apply</Button>
      </div>
    </CustomizePopover>
  );
};
